import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, LazyMotion, domAnimation, m, useReducedMotion } from 'motion/react';
import './registrationExperience.css';

interface ConfettiBurstProps {
  active: boolean;
  durationMs?: number;
}

interface ConfettiPiece {
  id: number;
  left: number;
  delay: number;
  duration: number;
  drift: number;
  rotation: number;
  color: string;
  shape: 'bar' | 'square';
}

const CONFETTI_COLORS = ['#48cae4', '#8beeff', '#4a8fe7', '#9a8cff', '#f6b768', '#f7fcff'];

function seededUnit(seed: number): number {
  const value = Math.sin(seed * 9301 + 49297) * 233280;
  return value - Math.floor(value);
}

function createPieces(): ConfettiPiece[] {
  return Array.from({ length: 30 }, (_, index) => ({
    id: index,
    left: 4 + seededUnit(index + 1) * 92,
    delay: seededUnit(index + 17) * 0.42,
    duration: 1.7 + seededUnit(index + 31) * 1.15,
    drift: -54 + seededUnit(index + 43) * 108,
    rotation: 180 + seededUnit(index + 59) * 520,
    color: CONFETTI_COLORS[index % CONFETTI_COLORS.length]!,
    shape: index % 3 === 0 ? 'square' : 'bar',
  }));
}

/** Lightweight, deterministic confetti that never captures pointer events. */
export function ConfettiBurst({ active, durationMs = 3400 }: ConfettiBurstProps): JSX.Element | null {
  const reduceMotion = useReducedMotion();
  const [visible, setVisible] = useState(active);
  const pieces = useMemo(createPieces, []);

  useEffect(() => {
    if (!active || reduceMotion) {
      setVisible(false);
      return undefined;
    }
    setVisible(true);
    const timer = window.setTimeout(() => setVisible(false), durationMs);
    return () => window.clearTimeout(timer);
  }, [active, durationMs, reduceMotion]);

  if (reduceMotion) return null;

  return (
    <LazyMotion features={domAnimation} strict>
      <AnimatePresence>
        {visible ? (
          <m.div
            className="fte-confetti"
            aria-hidden="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.16 }}
          >
            {pieces.map((piece) => (
              <m.i
                key={piece.id}
                className={`fte-confetti__piece fte-confetti__piece--${piece.shape}`}
                style={{ left: `${piece.left}%`, backgroundColor: piece.color }}
                initial={{ y: '-7dvh', x: 0, rotate: 0, opacity: 0 }}
                animate={{
                  y: '108dvh',
                  x: piece.drift,
                  rotate: piece.rotation,
                  opacity: [0, 1, 1, 0.88, 0],
                }}
                transition={{
                  delay: piece.delay,
                  duration: piece.duration,
                  ease: [0.22, 0.68, 0.28, 1],
                  times: [0, 0.08, 0.72, 0.9, 1],
                }}
              />
            ))}
          </m.div>
        ) : null}
      </AnimatePresence>
    </LazyMotion>
  );
}
